export async function processImageFile(file, {
    minWidth = 300,
    minHeight = 300,
    targetWidth = 500,
    targetHeight = 500,
    aspectRatio = 1,
    quality = 0.75,
    preserveOriginal = false,
} = {}) {
    const MAX_FILE_SIZE = 500 * 1024;

    if (preserveOriginal && file.size <= MAX_FILE_SIZE) {
        return file;
    }

    const imageDataUrl = await readFileAsDataURL(file);
    const img = await loadImage(imageDataUrl);
    const originalWidth = img.width;
    const originalHeight = img.height;

    if (originalWidth < minWidth || originalHeight < minHeight) {
        throw new Error(`Image must be at least ${minWidth} × ${minHeight}px`);
    }

    if (preserveOriginal) {
        const canvas = document.createElement("canvas");
        canvas.width = originalWidth;
        canvas.height = originalHeight;
        const ctx = canvas.getContext("2d");

        // Draw the full image without cropping
        ctx.drawImage(img, 0, 0, originalWidth, originalHeight);

        const qualities = [0.85, 0.80, 0.75];
        let lastFile;
        
        for (const q of qualities) {
            const dataUrl = canvas.toDataURL("image/jpeg", q);
            lastFile = dataURLtoFile(dataUrl, file.name.replace(/\.[^/.]+$/, ".jpg"));
            if (lastFile.size <= MAX_FILE_SIZE) {
                return lastFile;
            }
        }

        return lastFile;
    }

    // Calculate ideal crop dimensions for the desired aspect ratio
    const originalAspect = originalWidth / originalHeight;
    const targetAspect = aspectRatio;

    let sx, sy, sw, sh;
    if (originalAspect > targetAspect) {
        //  crop width
        sh = originalHeight;
        sw = sh * targetAspect;
        sx = (originalWidth - sw) / 2;
        sy = 0;
    } else {
        //  crop height
        sw = originalWidth;
        sh = sw / targetAspect;
        sx = 0;
        sy = (originalHeight - sh) / 2;
    }

    // Create canvas with target output size - resizing and cropping
    const canvas = document.createElement("canvas");
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    const ctx = canvas.getContext("2d");

    // Draw the cropped and scaled image
    ctx.drawImage(img, sx, sy, sw, sh, 0, 0, targetWidth, targetHeight);

    // Converts the canvas into a compressed JPEG image using the quality parameter.
    const dataUrl = canvas.toDataURL("image/jpeg", quality);
    return dataURLtoFile(dataUrl, file.name.replace(/\.[^/.]+$/, ".jpg"));
}

function readFileAsDataURL(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
}

function loadImage(dataUrl) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = dataUrl;
    });
}

function dataURLtoFile(dataUrl, filename) {
    const arr = dataUrl.split(",");
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) u8arr[n] = bstr.charCodeAt(n);
    return new File([u8arr], filename, { type: mime });
}
